import React, { useState, useEffect } from 'react';
import { useFormContext } from '../../context/FormContext';
import { PropertyBasics } from '../../types';
import { RequiredMark } from '../../lib/requiredFields';

const EXCLUSIVE_OPTIONS = new Set(['Unknown', 'None']);

export default function Section5PropertyBasics() {
  const { state, saveSection } = useFormContext();
  const [data, setData] = useState<PropertyBasics>({
    intake_id: '',
  });

  useEffect(() => {
    // Only initialize once — guard prevents re-running when formData updates after saves
    if (data.intake_id) return;
    if (state.formData.property_basics) {
      const loaded: any = { ...state.formData.property_basics };
      const hydrateList = (textKey: string, listKey: string) => {
        if (!loaded[listKey] && typeof loaded[textKey] === 'string' && loaded[textKey].trim()) {
          loaded[listKey] = loaded[textKey].split(',').map((s: string) => s.trim()).filter(Boolean);
        }
      };
      hydrateList('exterior_features', 'exterior_features_list');
      hydrateList('attic', 'attic_list');
      hydrateList('basement', 'basement_list');
      setData(loaded);
    } else if (state.intake) {
      setData(prev => ({ ...prev, intake_id: state.intake!.id }));
    }
  }, [state.formData.property_basics, state.intake]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (state.intake && data.intake_id) {
      saveSection('property_basics', data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, state.intake]);

  const updateField = (field: keyof PropertyBasics | string, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const isReq = (key: string) =>
    state.requiredFields.some(f => f.section === 'property_basics' && f.field_key === key);

  const isUnknown = (value: unknown) =>
    typeof value === 'string' && value.toLowerCase() === 'unknown';

  const currentYear = new Date().getFullYear();
  const roofNotOriginal = data.roof_original === false || data.roof_original === 0;

  const toggleFeatureList = (
    listKey: string,
    textKey: string,
    item: string,
    checked: boolean,
    otherKey?: string
  ) => {
    setData(prev => {
      const current: string[] = ((prev as any)[listKey] || []).filter(
        (x: string) => !EXCLUSIVE_OPTIONS.has(x) || item === x
      );
      let updated: string[];
      if (EXCLUSIVE_OPTIONS.has(item)) {
        updated = checked ? [item] : [];
      } else {
        const withoutExclusive = current.filter((x: string) => !EXCLUSIVE_OPTIONS.has(x));
        updated = checked
          ? [...withoutExclusive, item]
          : withoutExclusive.filter((x: string) => x !== item);
      }
      const next: any = {
        ...prev,
        [listKey]: updated,
        [textKey]: updated.join(', '),
      };
      if (checked && EXCLUSIVE_OPTIONS.has(item) && otherKey) {
        next[otherKey] = '';
      }
      return next;
    });
  };

  const unknownCheckbox = (
    field: string,
    current: unknown,
    asNumberInput = false
  ) => (
    <label className="flex items-center mt-2 cursor-pointer">
      <input
        type="checkbox"
        checked={isUnknown(current)}
        onChange={(e) => {
          if (e.target.checked) {
            updateField(field, 'unknown');
          } else {
            updateField(field, asNumberInput ? undefined : '');
          }
        }}
        className="mr-2"
      />
      <span className="text-sm text-gray-700">Unknown</span>
    </label>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Property Basics & Structure</h2>
        <p className="text-sm text-gray-600 mb-6">
          Fields marked with <span className="text-brand-coral">*</span> are required.
          Choose <span className="font-medium">Unknown</span> if you are not sure.
        </p>

        <div className="space-y-6">
          {/* Year Built */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year Built{isReq('year_built') && <RequiredMark />}
              </label>
              <input
                type="number"
                value={isUnknown(data.year_built) ? '' : (data.year_built ?? '')}
                onChange={(e) => updateField('year_built', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:bg-gray-100"
                placeholder="e.g., 1985"
                min="1800"
                max={currentYear}
                disabled={isUnknown(data.year_built)}
              />
              {unknownCheckbox('year_built', data.year_built, true)}
            </div>

            {/* Year Renovated — optional */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year Renovated (if applicable)
              </label>
              <input
                type="number"
                value={data.year_renovated || ''}
                onChange={(e) => updateField('year_renovated', parseInt(e.target.value) || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="e.g., 2015"
                min="1800"
                max={currentYear}
              />
            </div>
          </div>

          {/* Style */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Home Style{isReq('style') && <RequiredMark />}
            </label>
            <select
              value={data.style || ''}
              onChange={(e) => updateField('style', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="">Select style</option>
              <option value="colonial">Colonial</option>
              <option value="ranch">Ranch</option>
              <option value="split_level">Split Level</option>
              <option value="contemporary">Contemporary</option>
              <option value="traditional">Traditional</option>
              <option value="victorian">Victorian</option>
              <option value="cape_cod">Cape Cod</option>
              <option value="craftsman">Craftsman</option>
              <option value="tudor">Tudor</option>
              <option value="other">Other</option>
              <option value="unknown">Unknown</option>
            </select>
            {data.style === 'other' && (
              <input
                type="text"
                value={(data as any).style_other || ''}
                onChange={(e) => updateField('style_other' as any, e.target.value)}
                className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="Please specify home style"
              />
            )}
          </div>

          {/* Construction */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Construction Type{isReq('construction') && <RequiredMark />}
            </label>
            <select
              value={data.construction || ''}
              onChange={(e) => updateField('construction', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="">Select construction type</option>
              <option value="frame">Frame</option>
              <option value="brick">Brick</option>
              <option value="stone">Stone</option>
              <option value="stucco">Stucco</option>
              <option value="vinyl">Vinyl</option>
              <option value="concrete">Concrete</option>
              <option value="log">Log</option>
              <option value="mixed">Mixed</option>
              <option value="other">Other</option>
              <option value="unknown">Unknown</option>
            </select>
            {data.construction === 'other' && (
              <input
                type="text"
                value={(data as any).construction_other || ''}
                onChange={(e) => updateField('construction_other' as any, e.target.value)}
                className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="Please specify construction type"
              />
            )}
          </div>

          {/* Foundation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Foundation{isReq('foundation') && <RequiredMark />}
            </label>
            <select
              value={data.foundation || ''}
              onChange={(e) => updateField('foundation', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="">Select foundation type</option>
              <option value="block">Block</option>
              <option value="brick">Brick</option>
              <option value="concrete">Concrete</option>
              <option value="other">Other</option>
              <option value="unknown">Unknown</option>
            </select>
            {data.foundation === 'other' && (
              <input
                type="text"
                value={(data as any).foundation_other || ''}
                onChange={(e) => updateField('foundation_other' as any, e.target.value)}
                className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="Please specify foundation type"
              />
            )}
          </div>

          {/* Roof Information */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Roof Type{isReq('roof') && <RequiredMark />}
              </label>
              <select
                value={data.roof || ''}
                onChange={(e) => updateField('roof', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              >
                <option value="">Select roof type</option>
                <option value="asphalt_shingle">Asphalt Shingle</option>
                <option value="metal">Metal</option>
                <option value="tile">Tile</option>
                <option value="slate">Slate</option>
                <option value="wood_shake">Wood Shake</option>
                <option value="flat">Flat</option>
                <option value="other">Other</option>
                <option value="unknown">Unknown</option>
              </select>
              {data.roof === 'other' && (
                <input
                  type="text"
                  value={(data as any).roof_other || ''}
                  onChange={(e) => updateField('roof_other' as any, e.target.value)}
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  placeholder="Please specify roof type"
                />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Is this the original roof?{isReq('roof_original') && <RequiredMark />}
                </label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="roof_original"
                      checked={data.roof_original === true || data.roof_original === 1}
                      onChange={() => {
                        updateField('roof_original', true);
                        updateField('roof_year_replaced', undefined);
                      }}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="roof_original"
                      checked={data.roof_original === false || data.roof_original === 0}
                      onChange={() => updateField('roof_original', false)}
                      className="mr-2"
                    />
                    No
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="roof_original"
                      checked={data.roof_original === 'unknown'}
                      onChange={() => {
                        updateField('roof_original', 'unknown');
                        updateField('roof_year_replaced', undefined);
                      }}
                      className="mr-2"
                    />
                    Unknown
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year Roof Replaced
                  {isReq('roof_year_replaced') && roofNotOriginal && <RequiredMark />}
                  {!roofNotOriginal && (
                    <span className="text-gray-400 font-normal"> (if not original)</span>
                  )}
                </label>
                <input
                  type="number"
                  value={data.roof_year_replaced || ''}
                  onChange={(e) => updateField('roof_year_replaced', parseInt(e.target.value) || undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:bg-gray-100"
                  placeholder="e.g., 2018"
                  min="1800"
                  max={currentYear}
                  disabled={!roofNotOriginal}
                />
              </div>
            </div>
          </div>

          {/* Exterior Features */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Exterior Features{isReq('exterior_features') && <RequiredMark />}
            </label>
            <div className="space-y-2">
              {['French Doors', 'Underground Sprinkler', 'Patio', 'None', 'Unknown'].map((feature) => (
                <label key={feature} className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={((data as any).exterior_features_list || []).includes(feature)}
                    onChange={(e) =>
                      toggleFeatureList(
                        'exterior_features_list',
                        'exterior_features',
                        feature,
                        e.target.checked,
                        'exterior_features_other'
                      )
                    }
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">{feature}</span>
                </label>
              ))}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Other:</span>
                <input
                  type="text"
                  value={(data as any).exterior_features_other || ''}
                  onChange={(e) => {
                    const other = e.target.value;
                    setData(prev => {
                      const list: string[] = ((prev as any).exterior_features_list || [])
                        .filter((x: string) => !EXCLUSIVE_OPTIONS.has(x));
                      const joined = [...list, other.trim()].filter(Boolean).join(', ');
                      return {
                        ...prev,
                        exterior_features_other: other,
                        exterior_features_list: list,
                        exterior_features: joined || (other.trim() ? other.trim() : ''),
                      } as any;
                    });
                  }}
                  disabled={((data as any).exterior_features_list || []).some((x: string) => EXCLUSIVE_OPTIONS.has(x))}
                  className="flex-1 px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary text-sm disabled:bg-gray-100"
                  placeholder="e.g., deck, pergola, outdoor kitchen"
                />
              </div>
            </div>
          </div>

          {/* Ceiling Height */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ceiling Height{isReq('ceiling_height') && <RequiredMark />}
            </label>
            <input
              type="text"
              value={isUnknown(data.ceiling_height) ? '' : (data.ceiling_height || '')}
              onChange={(e) => updateField('ceiling_height', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:bg-gray-100"
              placeholder="e.g., 9ft, vaulted, varies by room"
              disabled={isUnknown(data.ceiling_height)}
            />
            {unknownCheckbox('ceiling_height', data.ceiling_height)}
          </div>

          {/* Siding */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Exterior Siding{isReq('siding') && <RequiredMark />}
            </label>
            <select
              value={data.siding || ''}
              onChange={(e) => updateField('siding', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="">Select siding type</option>
              <option value="shingle">Shingle</option>
              <option value="vinyl_siding">Vinyl Siding</option>
              <option value="shake">Shake</option>
              <option value="other">Other</option>
              <option value="unknown">Unknown</option>
            </select>
            {data.siding === 'other' && (
              <input
                type="text"
                value={(data as any).siding_other || ''}
                onChange={(e) => updateField('siding_other' as any, e.target.value)}
                className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="Please specify siding type"
              />
            )}
          </div>

          {/* Storage Spaces */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attic{isReq('attic') && <RequiredMark />}{' '}
                <span className="text-gray-400 font-normal">(check all that apply)</span>
              </label>
              <div className="space-y-2">
                {['Walk-up', 'Pull-down', 'Finished', 'Partially finished', 'None', 'Unknown'].map((item) => (
                  <label key={item} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={((data as any).attic_list || []).includes(item)}
                      onChange={(e) =>
                        toggleFeatureList('attic_list', 'attic', item, e.target.checked, 'attic_other')
                      }
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">{item}</span>
                  </label>
                ))}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Other:</span>
                  <input
                    type="text"
                    value={(data as any).attic_other || ''}
                    onChange={(e) => {
                      const other = e.target.value;
                      setData(prev => {
                        const list: string[] = ((prev as any).attic_list || [])
                          .filter((x: string) => !EXCLUSIVE_OPTIONS.has(x));
                        return {
                          ...prev,
                          attic_other: other,
                          attic_list: list,
                          attic: [...list, other.trim()].filter(Boolean).join(', '),
                        } as any;
                      });
                    }}
                    disabled={((data as any).attic_list || []).some((x: string) => EXCLUSIVE_OPTIONS.has(x))}
                    className="flex-1 px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary text-sm disabled:bg-gray-100"
                    placeholder=""
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Basement{isReq('basement') && <RequiredMark />}{' '}
                <span className="text-gray-400 font-normal">(check all that apply)</span>
              </label>
              <div className="space-y-2">
                {['Full', 'Fully finished', 'Partially finished', 'Exterior Access', 'Crawl space', 'None', 'Unknown'].map((item) => (
                  <label key={item} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={((data as any).basement_list || []).includes(item)}
                      onChange={(e) =>
                        toggleFeatureList('basement_list', 'basement', item, e.target.checked, 'basement_other')
                      }
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">{item}</span>
                  </label>
                ))}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Other:</span>
                  <input
                    type="text"
                    value={(data as any).basement_other || ''}
                    onChange={(e) => {
                      const other = e.target.value;
                      setData(prev => {
                        const list: string[] = ((prev as any).basement_list || [])
                          .filter((x: string) => !EXCLUSIVE_OPTIONS.has(x));
                        return {
                          ...prev,
                          basement_other: other,
                          basement_list: list,
                          basement: [...list, other.trim()].filter(Boolean).join(', '),
                        } as any;
                      });
                    }}
                    disabled={((data as any).basement_list || []).some((x: string) => EXCLUSIVE_OPTIONS.has(x))}
                    className="flex-1 px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary text-sm disabled:bg-gray-100"
                    placeholder=""
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Garage */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Garage{isReq('garage') && <RequiredMark />}
              </label>
              <select
                value={data.garage || ''}
                onChange={(e) => updateField('garage', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              >
                <option value="">Select</option>
                <option value="attached">Attached</option>
                <option value="detached">Detached</option>
                <option value="carport">Carport</option>
                <option value="none">None</option>
                <option value="other">Other</option>
                <option value="unknown">Unknown</option>
              </select>
              {data.garage === 'other' && (
                <input
                  type="text"
                  value={(data as any).garage_other || ''}
                  onChange={(e) => updateField('garage_other' as any, e.target.value)}
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  placeholder="Please specify garage type"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                # of Cars{isReq('garage_cars') && <RequiredMark />}
              </label>
              <input
                type="number"
                value={isUnknown((data as any).garage_cars) ? '' : ((data as any).garage_cars ?? '')}
                onChange={(e) => updateField('garage_cars' as any, e.target.value ? parseInt(e.target.value, 10) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:bg-gray-100"
                placeholder="e.g., 2"
                min="0"
                max="10"
                disabled={isUnknown((data as any).garage_cars)}
              />
              {unknownCheckbox('garage_cars', (data as any).garage_cars, true)}
            </div>
          </div>

          {/* Driveway */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Driveway Type{isReq('driveway') && <RequiredMark />}
            </label>
            <select
              value={data.driveway || ''}
              onChange={(e) => updateField('driveway', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="">Select driveway type</option>
              <option value="private">Private</option>
              <option value="shared">Shared</option>
              <option value="heated">Heated</option>
              <option value="none">None</option>
              <option value="other">Other</option>
              <option value="unknown">Unknown</option>
            </select>
            {data.driveway === 'other' && (
              <input
                type="text"
                value={(data as any).driveway_other || ''}
                onChange={(e) => updateField('driveway_other' as any, e.target.value)}
                className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="Please specify driveway type"
              />
            )}
          </div>
        </div>

        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            This structural information helps buyers understand the property&apos;s construction and features.
            Use Unknown when you are not sure — we can help fill in details later.
          </p>
        </div>
      </div>
    </div>
  );
}
